import React, { useEffect, useMemo } from 'react'
import styles from './Select.module.css'
import Label from '../Label'
import PropTypes from 'prop-types'
import uniqid from 'uniqid'
import Error from '../Error'
import { enLocale, getLocales } from '../../../utilities/locales'
import translate from '../../../utilities/translate'
import translations from '../../../translations/form'

// trans :: String -> String -> String
const trans = translate(translations)

const Select = React.forwardRef(({
  locale = enLocale,
  name,
  value,
  onChange,
  onError = () => {},
  label,
  required = false,
  disabled = false,
  error = '',
  children,
}, ref) => {
  useEffect(() => {
    if (required && (value === null || value.length === 0)) {
      onError(trans(locale)('errors.requiredField', { label }))
    } else {
      onError('')
    }
  }, [value])

  const id = useMemo(() => uniqid(), [name])
  const errorId = useMemo(() => uniqid(), [error])

  const handleChange = event => {
    onChange(event.target.value)
  }

  return (
    <div className={ styles.wrapper }>
      <Label htmlFor={ id } required={ required }>{ label }</Label>
      <select
        ref={ ref }
        className={ styles.select }
        id={ id }
        name={ name }
        value={ value }
        onChange={ handleChange }
        required={ required }
        aria-required={ required }
        aria-describedby={ error ? errorId : undefined }
        disabled={ disabled }
        aria-invalid={ error !== '' }
      >
        { children }
      </select>
      { error !== '' ? <Error id={ errorId }>{ error }</Error> : null }
    </div>
  )
})

const Option = ({ children, value }) =>
  <option
    value={ value }
    onClick={ e => e.stopPropagation() }
    onKeyDown={ e => e.stopPropagation() }
  >
    { children }
  </option>

Select.propTypes = {
  locale: PropTypes.oneOf(getLocales()),
  name: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]).isRequired,
  error: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onError: PropTypes.func,
  type: PropTypes.string,
  label: PropTypes.node.isRequired,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  children: PropTypes.node,
}

Option.propTypes = {
  children: PropTypes.node,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
}

Select.Option = Option

export default Select
